// Owners' side of the staff app and the white label: the company's own name, each Team member's code for the staff
// app (with the app's link as a QR code to scan), and the QR code to print for each checkpoint.
import { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import { crm, type CrmRecord, type StaffCode, type StaffEmail } from "../lib/crm";
import { T } from "../lib/i18n";
import { Icon, useStudio } from "./ui";

/** Sent on window when the company's name or brand changed, so the menu shows it at once. */
export const COMPANY_CHANGED = "vera-company";

const qr = (text: string, width = 320) => QRCode.toDataURL(text, { margin: 1, width, errorCorrectionLevel: "M" });

/** Shown on Home until an owner has named the company: the name its people and VERA use, and an optional brand. */
/**
 * Owners, on Home: who VERA writes to from the staff app (the managers), and the emails waiting for an owner. A staff
 * member's email to the managers goes out once they say yes; one to a client waits here, and the owner sends it from
 * their own mailbox (Open in email) after checking it.
 */
export function StaffMailCard() {
  const { getToken, toast } = useStudio();
  const [name, setName] = useState<string | null>(null), [brand, setBrand] = useState<string | null>(null);
  const [managers, setManagers] = useState(""), [saved, setSaved] = useState(""), [emails, setEmails] = useState<StaffEmail[]>([]), [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const c = await crm.company(getToken);
    setName(c.name); setBrand(c.brand);
    const list = (c.managers ?? []).join(", ");
    setManagers(list); setSaved(list);
    setEmails((await crm.staffOutbox(getToken).catch(() => ({ emails: [] as StaffEmail[] }))).emails);
  }, [getToken]);
  useEffect(() => { void load().catch(() => {}); }, [load]);
  useEffect(() => { const on = () => void load().catch(() => {}); window.addEventListener(COMPANY_CHANGED, on); return () => window.removeEventListener(COMPANY_CHANGED, on); }, [load]);
  if (!name) return null; // the company names itself first (SafetyCard, on Home)
  async function save() {
    setBusy(true);
    try {
      const r = await crm.saveCompany(getToken, name!, brand ?? undefined, managers.split(/[\s,;]+/).filter(Boolean));
      const list = (r.managers ?? []).join(", ");
      setManagers(list); setSaved(list); toast(T("Saved."));
    } catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }
  async function close(e: StaffEmail, action: "done" | "dismiss") {
    try { await crm.closeStaffEmail(getToken, e.id, action); setEmails((all) => all.filter((x) => x.id !== e.id)); } catch (err) { toast(err instanceof Error ? err.message : T("Something went wrong.")); }
  }
  const mailto = (e: StaffEmail) => `mailto:${e.to === "client" ? encodeURIComponent(e.address ?? "") : encodeURIComponent(saved.split(", ")[0] ?? "")}?subject=${encodeURIComponent(e.subject)}&body=${encodeURIComponent(e.body)}`;
  return (
    <section className="card stack">
      <h2>{T("Emails from the staff app")}</h2>
      <p className="mute">{T("VERA writes to these managers when staff report something and say yes. Emails to clients wait here for you to check and send.")}</p>
      <div className="row wrap" style={{ gap: 8 }}>
        <input className="input" style={{ flex: 1, minWidth: 220 }} value={managers} onChange={(e) => setManagers(e.target.value)} placeholder={T("Managers' email addresses, separated by commas")} />
        <button className="btn" disabled={busy || managers === saved} onClick={() => void save()}>{T("Save")}</button>
      </div>
      {emails.map((e) => (
        <div key={e.id} className="card stack" style={{ gap: 6 }}>
          <span className="faint small">{e.guardName} · {e.to === "client" ? `${T("To")} ${e.address}` : T("To the managers")}{e.why ? ` · ${T(e.why)}` : ""}</span>
          <b>{e.subject}</b>
          <p className="small" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{e.body}</p>
          <div className="row wrap" style={{ gap: 6 }}>
            <a className="btn sm primary" href={mailto(e)} onClick={() => void close(e, "done")}>{T("Open in email")}</a>
            <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(`${e.subject}\n\n${e.body}`); toast(T("Copied")); }}>{T("Copy")}</button>
            <button className="btn sm" onClick={() => void close(e, "dismiss")}>{T("Dismiss")}</button>
          </div>
        </div>))}
    </section>
  );
}

/** On a Team member: their code for the staff app, the app's link (and its QR code), a new PIN, or switching it off. */
export function StaffAccessCard({ guard }: { guard: CrmRecord }) {
  const { getToken, toast } = useStudio();
  const [code, setCode] = useState<StaffCode | null>(null), [link, setLink] = useState(""), [img, setImg] = useState(""), [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await crm.staffCodes(getToken);
    setLink(r.link);
    setCode(r.codes.find((c) => c.guardId === guard.id && !c.disabled) ?? null);
  }, [getToken, guard.id]);
  useEffect(() => { void load().catch(() => {}); }, [load]);
  useEffect(() => { if (link && code) void qr(link, 240).then(setImg); }, [link, code]);
  async function run(f: () => Promise<unknown>, done: string) {
    setBusy(true);
    try { await f(); await load(); toast(T(done)); } catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }
  return (
    <div className="wide staffcard">
      <dt>{T("Staff app")}</dt>
      <dd>{!code
        ? <button className="btn" disabled={busy} onClick={() => void run(() => crm.issueStaffCode(getToken, guard.id), "Code made. Give it to the person with the link.")}><Icon name="lock" size={14} /> {T("Give access to the staff app")}</button>
        : <div className="row wrap" style={{ gap: 14, alignItems: "flex-start" }}>
            {img && <img src={img} alt={T("QR code of the staff app's link")} width={120} height={120} style={{ background: "#fff", borderRadius: 8 }} />}
            <div className="stack" style={{ gap: 6 }}>
              <span>{T("Code")}: <b className="num" style={{ fontSize: 20, letterSpacing: "0.12em" }}>{code.code}</b></span>
              <span className="faint small">{code.hasPin ? T("In use: they have set their PIN.") : T("Not used yet: they set a PIN at their first sign-in. The code expires after 14 days unused.")}</span>
              <span className="faint small">{T("They scan the QR code or open")} <a href={link} target="_blank" rel="noreferrer noopener">{T("the link")}</a>{T(", sign in with the code, and add the app to their home screen.")}</span>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(`${link}\n${T("Code")}: ${code.code}`); toast(T("Copied")); }}>{T("Copy")}</button>
                {code.hasPin && <button className="btn sm" disabled={busy} onClick={() => void run(() => crm.staffCodeAction(getToken, code.code, "reset"), "They set a new PIN at their next sign-in.")}>{T("New PIN")}</button>}
                <button className="btn sm" disabled={busy} onClick={() => { if (confirm(T("Switch off this code? The person is signed out of the staff app at once."))) void run(() => crm.staffCodeAction(getToken, code.code, "disable"), "Switched off."); }}>{T("Switch off")}</button>
              </div>
            </div>
          </div>}
      </dd>
    </div>
  );
}

/** On a checkpoint: its QR code, to print and put up at the door. Staff scan it with the phone's camera. */
export function CheckpointQrCard({ checkpoint, siteName }: { checkpoint: CrmRecord; siteName?: string }) {
  const { getToken, toast } = useStudio();
  const [img, setImg] = useState(""), [busy, setBusy] = useState(false);
  const name = String(checkpoint.values.name ?? "");
  async function make() {
    setBusy(true);
    try { const r = await crm.checkpointQr(getToken, checkpoint.id); setImg(await qr(r.link, 480)); }
    catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }
  useEffect(() => { if (checkpoint.values.key) void make(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  /** A printable card: the code with the checkpoint's name and place under it. */
  async function download() {
    const pic = new Image(); pic.src = img; await pic.decode();
    const c = document.createElement("canvas"); c.width = 600; c.height = 760;
    const g = c.getContext("2d")!;
    g.fillStyle = "#fff"; g.fillRect(0, 0, c.width, c.height);
    g.drawImage(pic, 60, 40, 480, 480);
    g.fillStyle = "#111"; g.textAlign = "center"; g.font = "bold 34px system-ui, sans-serif"; g.fillText(name.slice(0, 32), 300, 580);
    g.font = "24px system-ui, sans-serif"; g.fillStyle = "#444";
    g.fillText([siteName, checkpoint.values.building, checkpoint.values.floor].filter(Boolean).join(" · ").slice(0, 44), 300, 625);
    g.font = "20px system-ui, sans-serif"; g.fillText(T("Scan with the phone's camera"), 300, 690);
    const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = `QR ${name || "checkpoint"}.png`; a.click();
  }
  return (
    <div className="wide">
      <dt>{T("QR code")}</dt>
      <dd>{img
        ? <div className="row wrap" style={{ gap: 14, alignItems: "flex-start" }}>
            <img src={img} alt={T("QR code of this checkpoint")} width={140} height={140} style={{ background: "#fff", borderRadius: 8 }} />
            <div className="stack" style={{ gap: 6 }}>
              <span className="faint small">{T("Print it and put it up at this place. Staff scan it with the phone's camera: the check lands here, and a problem goes to Issues.")}</span>
              <button className="btn sm" onClick={() => void download()}><Icon name="doc" size={14} /> {T("Download to print")}</button>
            </div>
          </div>
        : <button className="btn" disabled={busy} onClick={() => void make()}>{T("Make QR code")}</button>}
      </dd>
    </div>
  );
}
